// Loot and shops at the table (issue 1156), the client's half: the site's treasure and shop generators and the DM's
// saved shops, read by the server with the DM's own sign-in — every call here is to THIS origin (`requestJson`, so
// `authFetch`). Nothing of them arrives over the socket; an item reaches a sheet only through the #1055 give
// (`giveEntry` in `compendium.ts`).
import { requestJson } from './api';

/** One item of a hoard or a shop: its address on the site, its name and price as the site gave them, and whether it can be given. */
export interface LootItem { segment: string; slug: string; name: string; price: number | null; quantity: number; made: boolean; givable: boolean }
export interface Hoard {
  items: LootItem[];
  coins: Array<{ coin: string; amount: number }>;
  gems: Array<{ name: string; value: number | null }>;
  total: number | null;
}
export interface ShopKinds { kinds: Array<{ slug: string; name: string }>; sizes: Array<{ slug: string; name: string }> }
export interface SavedShopRow { id: string; name: string; itemCount: number }
export interface SavedShop { id: string; name: string; notes: string | null; wares: LootItem[] }

type Refused = { kind: 'refused'; reason: string } | { kind: 'error' };
export type Answer<T> = ({ kind: 'ok' } & T) | Refused;

const enc = encodeURIComponent;
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const isItem = (v: unknown): v is LootItem => isObj(v) && typeof v.segment === 'string' && typeof v.slug === 'string' && typeof v.name === 'string'
  && (v.price === null || typeof v.price === 'number') && typeof v.quantity === 'number' && typeof v.made === 'boolean' && typeof v.givable === 'boolean';
const items = (v: unknown): LootItem[] => (Array.isArray(v) ? v.filter(isItem) : []);
const refusedOr = (body: unknown): Refused => {
  const reason = (body as { reason?: unknown } | null)?.reason;
  return typeof reason === 'string' ? { kind: 'refused', reason } : { kind: 'error' };
};
const query = (q: Record<string, string | number | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

/** Roll a hoard from the site's treasure generator: by challenge rating or by budget, at most `maxItems` items. */
export async function rollHoard(campaignId: string, ask: { cr?: string; budget?: string; maxItems?: number }, fetchImpl: typeof fetch = fetch): Promise<Answer<{ hoard: Hoard }>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/loot${query({ cr: ask.cr, budget: ask.budget, max_items: ask.maxItems })}`, {}, fetchImpl);
  const h = isObj(body) && isObj(body.hoard) ? body.hoard : null;
  if (status === 200 && h && Array.isArray(h.items)) {
    const coins = Array.isArray(h.coins) ? h.coins.filter((c): c is { coin: string; amount: number } => isObj(c) && typeof c.coin === 'string' && typeof c.amount === 'number') : [];
    const gems = Array.isArray(h.gems) ? h.gems.filter((g): g is { name: string; value: number | null } => isObj(g) && typeof g.name === 'string') : [];
    return { kind: 'ok', hoard: { items: items(h.items), coins, gems, total: num(h.total) } };
  }
  return refusedOr(body);
}

/**
 * Where the hoard's challenge rating starts (qa-runs#1157): the party's average level read from the characters' sheets
 * (`party`), else the last number the DM rolled at in this campaign (`last`), else 1 (`first`). Null when it can't be read.
 */
export interface LootStart { cr: number; from: 'party' | 'last' | 'first' }
export async function readLootStart(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<LootStart | null> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/loot/start`, {}, fetchImpl);
  if (status !== 200 || !isObj(body) || num(body.cr) === null || !['party', 'last', 'first'].includes(body.from as string)) return null;
  return { cr: body.cr as number, from: body.from as LootStart['from'] };
}

/** Where a starting challenge rating came from, in words: "Party level 7", "Last used here"; nothing the first time. */
export const lootStartWords = (s: LootStart): string => (s.from === 'party' ? `Party level ${s.cr}` : s.from === 'last' ? 'Last used here' : '');

/** The site's shop kinds and settlement sizes for the table's system; both empty when it has none. */
export async function readShopKinds(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<Answer<ShopKinds>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/shop/kinds`, {}, fetchImpl);
  const pick = (v: unknown) => (Array.isArray(v) ? v.filter((k): k is { slug: string; name: string } => isObj(k) && typeof k.slug === 'string' && typeof k.name === 'string') : []);
  if (status === 200 && isObj(body)) return { kind: 'ok', kinds: pick(body.kinds), sizes: pick(body.sizes) };
  return refusedOr(body);
}

/** Stock a shop: of a kind in a settlement size (the site's settlement shop), or by budget. */
export async function stockShop(campaignId: string, ask: { kind?: string; size?: string; budget?: string; maxItems?: number }, fetchImpl: typeof fetch = fetch): Promise<Answer<{ wares: LootItem[] }>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/shop${query({ kind: ask.kind, size: ask.size, budget: ask.budget, max_items: ask.maxItems })}`, {}, fetchImpl);
  if (status === 200 && isObj(body) && Array.isArray(body.wares)) return { kind: 'ok', wares: items(body.wares) };
  return refusedOr(body);
}

/** The DM's saved shops on the site, for the table's system. */
export async function listSavedShops(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<Answer<{ shops: SavedShopRow[] }>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/shops`, {}, fetchImpl);
  if (status === 200 && isObj(body) && Array.isArray(body.shops)) {
    return { kind: 'ok', shops: body.shops.filter((s): s is SavedShopRow => isObj(s) && typeof s.id === 'string' && typeof s.name === 'string' && typeof s.itemCount === 'number') };
  }
  return refusedOr(body);
}

/** One saved shop and its wares. */
export async function readSavedShop(campaignId: string, shopId: string, fetchImpl: typeof fetch = fetch): Promise<Answer<{ shop: SavedShop }>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/shops/${enc(shopId)}`, {}, fetchImpl);
  const s = isObj(body) && isObj(body.shop) ? body.shop : null;
  if (status === 200 && s && typeof s.id === 'string' && typeof s.name === 'string') {
    return { kind: 'ok', shop: { id: s.id, name: s.name, notes: typeof s.notes === 'string' ? s.notes : null, wares: items(s.wares) } };
  }
  return refusedOr(body);
}

/** A row's key, for marking what was given: the same entry twice in a shop is one row. */
export const itemKey = (i: Pick<LootItem, 'segment' | 'slug'>): string => `${i.segment}/${i.slug}`;

// ── A hoard's coins and gems, given or held (qa-runs 1156) ──
// The DM's and a co-GM's with tokens. A held hoard is kept with the campaign's table, read here alone; a give is the
// DM's confirmed list, person by person, and whatever is not given stays held.
export interface HoardGem { name: string; value: number | null }
export type CoinCounts = Record<string, number>;
export interface HeldHoard { id: string; name: string; coins: CoinCounts; gems: HoardGem[]; items: LootItem[] }
export interface ShareAsk { characterId: string; coins: CoinCounts; gems: HoardGem[] }
export interface GiveOutcome {
  given: Array<{ character: { id: string; name: string }; coins: CoinCounts; gems: HoardGem[] }>;
  refused: Array<{ character: { id: string; name: string }; reason: string }>;
  held: HeldHoard | null;
  said: string;
}
/** A hoard's contents as the server takes them: coins by kind, gems, items without the "givable" mark. */
export interface HoardContents { coins: CoinCounts; gems: HoardGem[]; items: LootItem[] }

const coinsOf = (v: unknown): CoinCounts => {
  const out: CoinCounts = {};
  if (isObj(v)) for (const [k, n] of Object.entries(v)) if (typeof n === 'number' && n > 0) out[k] = n;
  return out;
};
const gemsOf = (v: unknown): HoardGem[] => (Array.isArray(v) ? v.filter((g): g is HoardGem => isObj(g) && typeof g.name === 'string').map((g) => ({ name: g.name, value: num(g.value) })) : []);
const heldOf = (v: unknown): HeldHoard | null => (isObj(v) && typeof v.id === 'string' && typeof v.name === 'string'
  ? { id: v.id, name: v.name, coins: coinsOf(v.coins), gems: gemsOf(v.gems), items: items(v.items) } : null);
const plainItems = (list: LootItem[]) => list.map(({ segment, slug, name, price, quantity, made }) => ({ segment, slug, name, price, quantity, made }));
const post = (body: unknown, method = 'POST'): RequestInit => ({ method, body: JSON.stringify(body) });

/** The table's held hoards, oldest first. */
export async function listHoards(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<Answer<{ hoards: HeldHoard[] }>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/hoards`, {}, fetchImpl);
  if (status === 200 && isObj(body) && Array.isArray(body.hoards)) return { kind: 'ok', hoards: body.hoards.map(heldOf).filter((h): h is HeldHoard => h !== null) };
  return refusedOr(body);
}

/** Hold a hoard for later under a name ("Goblin cave"). */
export async function holdHoard(campaignId: string, name: string, h: HoardContents, fetchImpl: typeof fetch = fetch): Promise<Answer<{ hoard: HeldHoard }>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/hoards`, post({ name, coins: h.coins, gems: h.gems, items: plainItems(h.items) }), fetchImpl);
  const hoard = isObj(body) ? heldOf(body.hoard) : null;
  return status === 201 && hoard ? { kind: 'ok', hoard } : refusedOr(body);
}

/** Rename a held hoard. */
export async function renameHoard(campaignId: string, id: string, name: string, fetchImpl: typeof fetch = fetch): Promise<Answer<{ hoard: HeldHoard }>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/hoards/${enc(id)}`, post({ name }, 'PUT'), fetchImpl);
  const hoard = isObj(body) ? heldOf(body.hoard) : null;
  return status === 200 && hoard ? { kind: 'ok', hoard } : refusedOr(body);
}

/**
 * Give one of a held hoard's items to a character whose token is on the board: given, it leaves the hoard (one fewer,
 * or gone), and the answer carries the hoard as it now stands (null: nothing is left in it, so it is gone).
 */
export async function giveHeldItem(campaignId: string, hoardId: string, a: { characterId: string; segment: string; slug: string; sceneId: string | null }, fetchImpl: typeof fetch = fetch): Promise<Answer<{ said: string; held: HeldHoard | null }>> {
  const body = { characterId: a.characterId, segment: a.segment, slug: a.slug, ...(a.sceneId ? { scene_id: a.sceneId } : {}) };
  const { status, body: got } = await requestJson(`/api/tables/${enc(campaignId)}/hoards/${enc(hoardId)}/give-item`, post(body), fetchImpl);
  if (status === 201 && isObj(got) && typeof got.said === 'string') return { kind: 'ok', said: got.said, held: heldOf(got.held) };
  return refusedOr(got);
}

/** Discard a held hoard: nothing in it is given to anyone. */
export async function discardHoard(campaignId: string, id: string, fetchImpl: typeof fetch = fetch): Promise<Answer<{ said: string }>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/hoards/${enc(id)}`, { method: 'DELETE' }, fetchImpl);
  if (status === 200 && isObj(body) && typeof body.said === 'string') return { kind: 'ok', said: body.said };
  return refusedOr(body);
}

/** Give the confirmed shares from a held hoard (`hoardId`) or a fresh roll (`hoard`, its rest held as `name`). */
export async function giveHoard(campaignId: string, ask: { hoardId: string } | { hoard: HoardContents; name?: string }, shares: ShareAsk[], fetchImpl: typeof fetch = fetch): Promise<Answer<GiveOutcome>> {
  const from = 'hoardId' in ask ? { hoardId: ask.hoardId } : { hoard: { coins: ask.hoard.coins, gems: ask.hoard.gems, items: plainItems(ask.hoard.items) }, ...(ask.name ? { name: ask.name } : {}) };
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/hoards/give`, post({ ...from, shares }), fetchImpl);
  if (status === 200 && isObj(body)) {
    const who = (v: unknown) => (isObj(v) && typeof v.id === 'string' && typeof v.name === 'string' ? { id: v.id, name: v.name } : { id: '', name: '' });
    return {
      kind: 'ok',
      given: Array.isArray(body.given) ? body.given.filter(isObj).map((g) => ({ character: who(g.character), coins: coinsOf(g.coins), gems: gemsOf(g.gems) })) : [],
      refused: Array.isArray(body.refused) ? body.refused.filter(isObj).map((r) => ({ character: who(r.character), reason: typeof r.reason === 'string' ? r.reason : '' })) : [],
      held: heldOf(body.held),
      said: typeof body.said === 'string' ? body.said : '',
    };
  }
  return refusedOr(body);
}

// ── The live word that a held hoard changed (qa-runs 1197) ─────────────────
// `table:hoards` reaches the DM's and the co-GMs' sockets alone, no word of a hoard in it; the open Loot tool reads its
// held list again.
type HoardsListener = (campaignId: string) => void;
const hoardsListeners = new Set<HoardsListener>();
/** Called when the socket says a held hoard at this table was held, renamed, given from or discarded. */
export function hoardsChanged(campaignId: string): void { for (const l of hoardsListeners) l(campaignId); }
/** Listen for that word; the returned function stops listening. */
export function onHoardsChanged(l: HoardsListener): () => void { hoardsListeners.add(l); return () => { hoardsListeners.delete(l); }; }
